/**
 * The glossary behind every ⓘ on the Revenue page. One entry per
 * datapoint: what it is, exactly how it's calculated, and which
 * SharePoint columns feed it. If a definition here is wrong, the
 * calculation is wrong - flag it.
 */

const WIP = "Work in Progress list · TO (£), Month, Year, Status, DOI";
const LEDGER_RULES =
  "CANCELLED and zero-value rows excluded; credit notes (negative TO) included; history starts Jan 2023. An instalment's month is its Month + Year columns (date of invoice as fallback).";
const FGP_RULE =
  "FGP (£) is the per-instalment profit figure maintained by hand: an estimate (~40-45%) while the project runs, overwritten with the verified profit when the project is finalised (Status flips to INVOICED/FINISHED). GP (£) is a Power Automate stamp of Cognito's project-level ProfitIn - only used as a project total, spread across instalments by value share, when a project has no FGP at all.";

export const METRIC_DEFS = {
  // ------------------------------------------------------ Overview
  ov_billed_ytd: {
    title: "Billed · period to date",
    what: "Revenue actually invoiced in the selected period so far (year, quarter or month, via the toggle).",
    how: `Sum of TO (£) for instalments dated before today within the selected period. The ▲/▼ badge compares against the same period last year at the same point (full months plus a pro-rated current month). The full-year ladder below shows every year on the ledger. ${LEDGER_RULES}`,
    source: WIP,
  },
  ov_scheduled_roy: {
    title: "Scheduled · to end of year",
    what: "Invoices already contracted and scheduled between today and 31 December.",
    how: "Sum of TO (£) for instalments dated today or later, falling on or before December of the current year. No pipeline or quotes - only instalment rows that exist on the ledger.",
    source: WIP,
  },
  ov_year_committed: {
    title: "Full year · committed",
    what: "Where the calendar year lands on ledger facts alone: billed so far plus every instalment scheduled to 31 December.",
    how: "Billed year-to-date + instalments dated today or later falling on or before December of the current year. No pipeline, no quotes - only rows that exist on the Work in Progress ledger.",
    source: WIP,
  },
  ov_projected: {
    title: "Projected billings",
    what: "What the live quoted pipeline is expected to actually invoice before the selected period ends (year, quarter or month, via the toggle).",
    how: "Three empirical steps, no assumptions. (1) Each live QUOTED opportunity gets its win probability from the likelihood model. (2) Win timing comes from the study type's historical win-lag curve, conditioned on how long the quote has already been open. (3) Billing timing comes from the ledger itself - the measured share of project value invoiced N days after commissioning (roughly 31% within 90 days, 51% within 150). Chained together: probability × share of value that would invoice by the period end. Shorter horizons capture less of every win's billing tail, so the quarter and month figures are naturally much smaller than the year figure. The small line shows the full pipeline value regardless of timing.",
    source: "SharePoint pipeline + likelihood model + WIP ledger",
  },
  ov_year_total: {
    title: "Full year · with projected",
    what: "The committed full-year figure plus what the quoted pipeline is expected to invoice before 31 December.",
    how: "Committed (billed year-to-date + scheduled to 31 December) + the in-year projected billings. The projected element is timing-aware and statistical, so read this as the expected full-year outcome, not a promise.",
    source: WIP,
  },
  ov_scheduled_next_year: {
    title: "Scheduled · next year",
    what: "Invoices already contracted with instalment dates falling anywhere in the next calendar year.",
    how: "Sum of TO (£) for instalments dated today or later whose month falls in the next calendar year (January to December). Only rows that exist on the ledger. The small line adds the projected spill: pipeline value expected to be won but too late to invoice this year.",
    source: WIP,
  },
  ov_billed_12m: {
    title: "Billed · last 12 months",
    what: "Revenue actually invoiced over the last 12 complete months.",
    how: `Sum of TO (£) for instalments dated before today whose month falls in the last 12 complete months - the current partial month is excluded. The ▲/▼ badge compares against the 12 months before those. ${LEDGER_RULES}`,
    source: WIP,
  },
  ov_billed_total: {
    title: "Billed · since Jan 2023",
    what: "Everything invoiced since the ledger starts.",
    how: `Sum of TO (£) for every instalment dated before today, from Jan 2023. ${LEDGER_RULES}`,
    source: WIP,
  },
  ov_scheduled_12m: {
    title: "Scheduled · next 12 months",
    what: "Invoices already contracted and scheduled to be issued in the coming year.",
    how: "Sum of TO (£) for instalments dated today or later, falling within the next 12 months. No pipeline or quotes - only instalment rows that exist on the ledger.",
    source: WIP,
  },
  ov_forward_total: {
    title: "Total forward book",
    what: "Every future instalment on the ledger, however far out.",
    how: "Sum of TO (£) for ALL instalments dated today or later - including those beyond the 12-month chart window.",
    source: WIP,
  },
  ov_pacing: {
    title: "Pacing",
    what: "Is this month / quarter tracking ahead or behind the same period last year?",
    how: "Projection = billed so far (instalments dated before today) + scheduled remainder (instalments dated today or later within the period). The ▲/▼ badge and the dark marker on the bar compare that projection against the same calendar period last year (billed actuals). The forward rows underneath (next 6 months on the month card, next 2 quarters on the quarter card) compare what is ALREADY SCHEDULED in each future period against the same period last year's billed total - future periods keep filling as new work is commissioned, so a shortfall there is a pipeline prompt, not a verdict.",
    source: WIP,
  },
  ov_light_months: {
    title: "Light months",
    what: "Months ahead where too little billing is scheduled - new work must land there or cash dips.",
    how: "Run-rate = average billed per month over the last 6 complete months. Each of the next 6 months is flagged when its scheduled billing is below 60% of that run-rate. The gap shown is run-rate minus scheduled.",
    source: WIP,
  },
  ov_timeline: {
    title: "Cash-flow timeline",
    what: "Billed history and the forward invoice schedule on one axis.",
    how: `Teal = instalments dated before today (billed); light blue = dated today or later (scheduled). Quarterly view sums the same months. Click any bar to see the instalments behind it. ${LEDGER_RULES}`,
    source: WIP,
  },
  ov_sales_kpis: {
    title: "Sales KPIs",
    what: "The board workbook wishlist, computed live: average price per commissioned project, size-band mix, win rate and median time to close.",
    how: "Trailing 12 months of Schedule of Work COM dates, one count per opportunity (re-quotes collapse). Win rate = share of opportunities first quoted in the 12-month window ending 90 days ago that have since been commissioned - young quotes are excluded so the rate is not dragged down by proposals still in play. Time to close = median days from the first quote in a chain to its COM date. MediLAB orders are excluded.",
    source: "Schedule of Work list · proposal and COM dates, turnover",
  },
  pnl_cascade: {
    title: "The P&L cascade",
    what: "Revenue stepping down to EBITDA the way the board pack reads it: Revenue, Cost of sales, Gross profit, Direct people, Contribution, Overheads, EBITDA.",
    how: "Built from the account-numbered chart in QuickBooks: 5xxx accounts are project cost of sales, 6xxx accounts (delivery salaries, NIC, pension, remote agency) are Direct people even though they sit inside COGS, 7xxx expense accounts are Overheads. Depreciation and amortisation are added back to EBITDA. Investment gains and other non-operating items are excluded.",
    source: "QuickBooks Online · ProfitAndLoss report by account",
  },
  pnl_ltm: {
    title: "LTM trend",
    what: "Last-twelve-months revenue, EBITDA and EBITDA margin - the smoothed trend line of a lumpy business.",
    how: "Each point sums the twelve months ending there, so seasonality (the December spike) washes out and the direction of travel is unmistakable. This is the lens the Big 5 valuation metrics and the plan targets use. The last point includes the current partial month.",
    source: "QuickBooks Online · ProfitAndLoss report",
  },
  cash_war_chest: {
    title: "War chest",
    what: "The full liquidity picture: operating cash plus investment portfolios plus venture holdings.",
    how: "Operating cash = live bank balances (what the runway and 13-week forecast use). Investment portfolios = the long-term FAS investment accounts. Venture holdings = named illiquid positions (Measure Protocol, The Conservatory, Monitree App). Balances are the current QuickBooks account balances, so portfolio values are only as fresh as the last revaluation posted by the accountant.",
    source: "QuickBooks Online · account balances",
  },
  cash_wc_days: {
    title: "Working capital days",
    what: "How many days of billing sit in debtors (DSO) and how many days of supplier spend sit in creditors (DPO).",
    how: "Countback method, as the board workbook used: walk backwards through the most recent months of gross invoicing (or supplier bills) until the current receivables (or payables) balance is exhausted; the days walked is the answer. Gross of VAT on both sides, matching the balances. A big DSO-DPO gap means the company banks its clients while paying suppliers promptly.",
    source: "QuickBooks Online · open balances + monthly invoice and bill totals",
  },
  div_revenue: {
    title: "Revenue by division",
    what: "Billed revenue split by division (QBO Class): UK & Sight, International, PSE, MLab, PMS.",
    how: "Straight from the QuickBooks ClassSales report, monthly since 2021, stacked by division. Admin absorbs unclassified lines. This is billed P&L revenue, a different lens from the COM-date sales view on the Overview tab.",
    source: "QuickBooks Online · ClassSales report",
  },
  div_waterfall: {
    title: "Quarter waterfall",
    what: "Which divisions drove the change between a quarter and the same quarter last year.",
    how: "Starts at last year's quarter total, steps through each division's year-on-year change (teal up, red down) and lands on this year's total. Pick any of the last eight full quarters. The board workbook drew this by hand each quarter; here it is generated live.",
    source: "QuickBooks Online · ClassSales report",
  },
  out_big5: {
    title: "The Big 5",
    what: "The five valuation metrics the plan is written in: LTM Revenue, LTM EBITDA, EBITDA margin, and both growth rates, against the Go for Gold (Dec 2026) and Exit (Dec 2027) targets.",
    how: "Actuals come live from the QuickBooks LTM story (rolling twelve months to the latest month). Targets are the plan anchors from the board workbook: £6m revenue and £1.5m EBITDA at 25% margin for FY26, £7m and £2m at 28.6% for FY27, 10% growth on both lines. The progress bar shows actual over the FY26 target.",
    source: "QuickBooks Online · LTM story + plan targets",
  },
  ov_sales: {
    title: "Sales · new work commissioned",
    what: "The value of new projects commissioned each month and each quarter, with the same period last year for comparison.",
    how: "Each project counts once, in the period its Schedule of Work COM DATE falls; re-quotes and revisions collapse into their originating quote, matching the Quarter at a Glance dedup. Value = the turnover quoted on the commissioned version. The dashed line is the same month or quarter one year earlier. MediLAB orders are excluded - they sit outside the Schedule of Work. Click a bar for the projects behind it.",
    source: "Schedule of Work list · COM DATE, turnover",
  },
  ov_billed_per_year: {
    title: "Billed per year",
    what: "Annual billed totals with year-on-year growth.",
    how: "Complete years compare full year vs prior full year. The current year (*) compares billed-to-date against the prior year at the same point - prior complete months plus the current month pro-rated by day of month (÷ 30.44).",
    source: WIP,
  },

  // -------------------------------------------------- Profitability
  prof_basis: {
    title: "Profit basis",
    what: "Which projects the margins below stand on.",
    how: "All: every instalment with a profit figure - verified and estimated. Verified only: projects whose Status is INVOICED or FINISHED, where FGP (£) holds the real, accounts-checked profit. Live estimates only: projects still at BRIEFED / SOW SIGNED / IN FIELD, where FGP is still your working estimate.",
    source: "Work in Progress list · FGP (£), Status",
  },
  prof_waterfall: {
    title: "Predicted profit on scheduled invoices",
    what: "Scheduled (future-dated) instalments never carry FGP yet - you enter it at issue - so their profit is predicted from the best evidence available.",
    how: "In order: (1) the project's own estimated margin from its issued instalments; (2) the project-level Cognito ProfitIn stamp, spread across instalments by value share; (3) (that client's verified margin + 45%) ÷ 2; (4) 45% flat for clients with no history. Every predicted figure is tagged with its rung in drill-downs. Predictions recompute live as projects get finalised, and never touch past-dated rows.",
    source: "WiP FGP (£) + GP (£) stamp + verified client history",
  },
  prof_revenue: {
    title: "Revenue · since Jan 23",
    what: "Billed revenue over the whole ledger.",
    how: `Sum of TO (£) for instalments in months up to and including the current month. ${LEDGER_RULES}`,
    source: WIP,
  },
  prof_gp: {
    title: "Gross profit · since Jan 23",
    what: "Gross profit on billed instalments under the chosen basis.",
    how: `Sum of profit per instalment for months up to and including the current month. ${FGP_RULE} Past instalments with no profit signal contribute nothing.`,
    source: "Work in Progress list · FGP (£), GP (£)",
  },
  prof_margin: {
    title: "Margin · last 12 months",
    what: "Gross margin over the last 12 complete months, with its direction of travel.",
    how: "Profit ÷ profit-known revenue × 100 for instalments in the last 12 complete months (current partial month excluded). The ▲/▼ badge compares against the 12 months before that. The sublabel also shows the all-time margin since Jan 23 and how much revenue the margin stands on (profit-known coverage).",
    source: "Work in Progress list · FGP (£), TO (£), Status",
  },
  prof_point_value: {
    title: "1 margin point",
    what: "What one percentage point of margin is worth in pounds per year.",
    how: "Last-12-months billed revenue ÷ 100. It makes margin conversations concrete: winning back two points on a weak account is worth twice this number, every year.",
    source: "Work in Progress list · TO (£)",
  },
  prof_movers: {
    title: "Margin movers",
    what: "The clients and study types whose margin swung the most, and what the swing costs or earns.",
    how: "For each client and study type with at least £25k of profit-known revenue in BOTH windows: margin over the last 12 complete months minus margin over the 12 months before, in points. £ impact = the swing × last-12-months revenue. The biggest absolute swings are shown, positive or negative.",
    source: "Work in Progress list · FGP (£), TO (£)",
  },
  prof_bands: {
    title: "Margin distribution",
    what: "How the project book is shaped - how many projects sit in each margin band, and how much revenue rides on each band.",
    how: "Per project: profit ÷ profit-known revenue × 100 under the chosen basis, bucketed into bands. A healthy book has its mass at 40-60% with a thin left tail; a fat sub-40% tail means the blended average hides a pricing problem.",
    source: WIP,
  },
  prof_margin_chart: {
    title: "Revenue & margin per month",
    what: "Monthly revenue bars with the margin line - and where that line is fact vs estimate.",
    how: "Bars = TO (£) per month (lighter = future months). Line = profit ÷ profit-known revenue per month. Amber zones mark months where LESS than 50% of the revenue sits on verified (finalised) projects - the margin there is mostly still estimated. Hover a month for its verified share. Future months use predicted profit (see the waterfall ⓘ).",
    source: WIP,
  },
  prof_league_client: {
    title: "Margin by client",
    what: "Revenue, profit and margin per client - top 25 by revenue.",
    how: "All instalments since Jan 2023 grouped by client, under the chosen basis. Margin uses profit-known revenue as denominator per client.",
    source: WIP,
  },
  prof_league_team: {
    title: "Margin by team",
    what: "Revenue, profit and margin per team.",
    how: "All instalments since Jan 2023 grouped by the ledger's Team column, under the chosen basis.",
    source: "Work in Progress list · Team column",
  },
  prof_league_study: {
    title: "Margin by study type",
    what: "Revenue, profit and margin per study type.",
    how: "All instalments since Jan 2023 grouped by the ledger's Study Type column, under the chosen basis.",
    source: "Work in Progress list · Study Type column",
  },
  prof_loss: {
    title: "Projects below 25%",
    what: "Projects earning less than a 25% margin, split into ones you can still act on and ones that are done.",
    how: "Per project: profit ÷ profit-known revenue × 100 under the chosen basis (projects with under £500 of profit-known revenue are skipped). LIVE projects come first, ranked by the £ shortfall against the 45% benchmark, and show how much revenue is still to invoice - that is where profit can still be protected by chasing scope creep or re-quoting extensions. Finished projects sit in the collapsed lessons list, ranked worst first.",
    source: WIP,
  },

  // ------------------------------------------------------ Explorer
  exp_dimension: {
    title: "Cut by",
    what: "The dimension the ledger is grouped by.",
    how: "Team, PM and Study type come straight off the ledger row (blank PM shows as 'Unassigned'). Client groups by client number. Qual/Quant is joined from the Schedule of Work via CID ('Unspecified' when no match).",
    source: WIP,
  },
  exp_grain: {
    title: "Grain",
    what: "The period each column represents.",
    how: "An instalment's period comes from its Month + Year columns; quarters are calendar quarters (Q1 = Jan-Mar).",
    source: WIP,
  },
  exp_metric: {
    title: "Metric",
    what: "What each cell shows.",
    how: "Revenue = sum of TO (£). Gross profit = sum of profit per instalment under the chosen basis. Margin % = profit ÷ profit-known revenue × 100 per cell.",
    source: WIP,
  },
  exp_basis: {
    title: "Profit basis",
    what: "Which projects feed the GP and margin metrics.",
    how: `All: every instalment with a profit figure. Verified only: finalised projects (Status INVOICED/FINISHED). Live estimates only: projects still running. ${FGP_RULE}`,
    source: "Work in Progress list · FGP (£), GP (£), Status",
  },
  exp_scope: {
    title: "Scope",
    what: "Whether future instalments are included.",
    how: "Billed only = instalments dated before today (matches the Overview and Outlook 'billed' rule). Billed + scheduled adds future-dated instalments.",
    source: WIP,
  },
  exp_matrix: {
    title: "Explorer matrix",
    what: "The ledger cut by your chosen dimension × period.",
    how: `Rows are capped at the top 40 by revenue; everything else rolls into 'All others' so the totals always reconcile with the ledger. Click any cell for the underlying invoices. ${LEDGER_RULES}`,
    source: WIP,
  },

  // ------------------------------------------------------- Clients
  cl_top5: {
    title: "Top-5 client share",
    what: "How dependent the year's revenue is on the biggest 5 clients.",
    how: "Clients ranked by billed revenue within the year; the top 5's combined billed £ ÷ the year's total billed £ × 100.",
    source: WIP,
  },
  cl_top10: {
    title: "Top-10 client share",
    what: "Same dependency measure, top 10 clients.",
    how: "The 10 largest clients' combined billed £ ÷ the year's total billed £ × 100.",
    source: WIP,
  },
  cl_active: {
    title: "Active clients",
    what: "Clients billed at least once in the year.",
    how: "Count of distinct client numbers with any billed instalment in the year.",
    source: WIP,
  },
  cl_repeat: {
    title: "Repeat revenue share",
    what: "How much of the year's revenue comes from returning clients.",
    how: "A client is 'new' in the year they are billed for the first time EVER (pre-2023 history is used to classify, but its £ is never counted). Repeat share = revenue from previously-billed clients ÷ the year's total billed.",
    source: WIP,
  },
  cl_concentration: {
    title: "Concentration by year",
    what: "Top-5 / top-10 dependency trend, year by year.",
    how: "For each year: clients ranked by billed £; top-5 and top-10 combined share of that year's billed total.",
    source: WIP,
  },
  cl_new_repeat: {
    title: "New vs repeat revenue",
    what: "Revenue split by whether the client had ever been billed before.",
    how: "'New' = billed £ from clients whose first-ever billed year is this year (classification looks at pre-2023 history too). Everything else is repeat.",
    source: WIP,
  },
  cl_movers: {
    title: "Client movers",
    what: "Top 40 clients by all-time billed revenue, with per-year totals.",
    how: "Δ compares the last two COMPLETE years (current partial year shown but not used for the delta). 'Committed' = current-year billed plus ledger instalments still scheduled before 31 Dec (the '+£X due' part). 'Quiet 6m+' = nothing billed in the last 6 months.",
    source: WIP,
  },

  // ------------------------------------------------------- Outlook
  out_ytd: {
    title: "Billed YTD",
    what: "What has actually been invoiced so far this year.",
    how: "Sum of TO (£) for current-year instalments dated before today. The pace badge compares against the prior year at the same point: its complete months plus its same-month pro-rated by day of month (÷ 30.44).",
    source: WIP,
  },
  out_remainder: {
    title: "Contracted remainder",
    what: "Current-year invoices already scheduled but not yet issued.",
    how: "Sum of TO (£) for current-year instalments dated today or later. Contracted work only - no quotes, no pipeline.",
    source: WIP,
  },
  out_projection: {
    title: "Projected landing",
    what: "Where the year lands if nothing new is won.",
    how: "Billed YTD + contracted remainder. Deliberately conservative: pipeline and quotes are never mixed in (see the pipeline bridge for that view).",
    source: WIP,
  },
  out_target: {
    title: "Target",
    what: "Your manual revenue target for the year, tracked against the contracted projection.",
    how: "'On track' when projection ≥ target. Otherwise the gap = target − projection, and required monthly new business = gap ÷ months left in the year.",
    source: "Set manually on this page · compared against the WiP ledger",
  },
  out_cumulative: {
    title: "Cumulative revenue by year",
    what: "Each year's running total, month by month, on one axis.",
    how: "Running sum of billed TO (£) per month within each year. The current year's line stops at the current month.",
    source: WIP,
  },
  out_bridge: {
    title: "Pipeline bridge",
    what: "What the live quoted book could add on top of the contracted projection.",
    how: "Quoted book = open quotes from the last 6 months on the Schedule of Work. Weighted pipeline = Σ quote value × its likelihood score. Win rate needed = (target − projection) ÷ quoted total. Indicative only - never mixed into revenue figures.",
    source: "Schedule of Work quoted book · likelihood-weighted",
  },
  out_seasonality: {
    title: "Quarterly seasonality",
    what: "How billing distributes across quarters, year by year.",
    how: "Billed TO (£) grouped by calendar quarter within each year. Current-year quarters are partial.",
    source: WIP,
  },
  fq_quality: {
    title: "Forward book quality",
    what: "How solid the next 12 months of scheduled billing is.",
    how: "Scheduled instalments (dated today or later, next 12 months) stacked by the project's CURRENT status. IN FIELD money is nearly certain; SOW SIGNED sits further from delivery.",
    source: "Work in Progress list · Status column",
  },
  fq_concentration: {
    title: "Concentration risk",
    what: "How much of the future depends on a handful of clients.",
    how: "The 5 largest clients in the ENTIRE forward book (all future instalments, not just 12 months) - their combined £ ÷ the total forward book × 100.",
    source: WIP,
  },
  fwd_stage: {
    title: "Forward book by stage",
    what: "The whole forward book grouped by project status.",
    how: "All instalments dated today or later, grouped by the project's current Status column. Instalments on projects already at INVOICED are final/post-dated invoices.",
    source: "Work in Progress list · Status column",
  },
  next_90: {
    title: "Next 90 days",
    what: "The individual invoices due to go out in the next quarter.",
    how: "Every instalment dated between today and +90 days, sorted by date. 'Instalment n of m' uses the FIID sequence and the project's expected invoice count.",
    source: WIP,
  },

  // ------------------------------------------------------ Velocity
  vel_q2c: {
    title: "Quote → first cash",
    what: "How long it takes to turn a proposal into the first invoice.",
    how: "Per project: days from the Schedule of Work proposal date to the project's FIRST billed instalment. The figure shown is the median. Negative gaps and gaps over 1,500 days are excluded as data errors.",
    source: "SoW proposal date + WiP first instalment date, joined on CID",
  },
  vel_cover: {
    title: "Months of cover",
    what: "How long the contracted forward book would sustain the current billing pace.",
    how: "Forward contracted ÷ monthly run-rate. Below ~4 months means new sales are needed soon to keep the pace.",
    source: WIP,
  },
  vel_runrate: {
    title: "Monthly run-rate",
    what: "The current billing pace.",
    how: "Average billed TO (£) per month over the last 6 complete months.",
    source: WIP,
  },
  vel_forward: {
    title: "Forward contracted",
    what: "Everything scheduled in months after the current one.",
    how: "Sum of TO (£) for instalments in months strictly after the current month.",
    source: WIP,
  },
  vel_b2b: {
    title: "Book-to-bill",
    what: "Are we winning work faster than we're billing it?",
    how: "Booked = Schedule of Work turnover by the quarter the project was commissioned. Billed = WiP instalments by their Month/Year quarter. Ratio = booked ÷ billed; above 1 means the order book is growing. The last quarter is partial.",
    source: "SoW commission date + turnover vs WiP ledger",
  },
  vel_q2c_team: {
    title: "Quote → first cash by team",
    what: "The same median, split by team.",
    how: "Median days per team (teams with fewer than 5 measured projects are hidden as too noisy).",
    source: "SoW proposal date + WiP first instalment date, joined on CID",
  },

  // ----------------------------------------------------- Integrity
  int_checked: {
    title: "Projects cross-checked",
    what: "How many projects the integrity checks cover.",
    how: "Every distinct CID with instalment rows on the WiP ledger (cancelled rows excluded).",
    source: "WiP ledger vs Schedule of Work, joined on CID",
  },
  int_mismatch: {
    title: "Value mismatches",
    what: "Projects whose instalments don't add up to the project's value.",
    how: "Per CID: sum of WiP instalments vs the SoW turnover. Flagged when the difference exceeds £500 or 5% of the project value, whichever is larger.",
    source: "WiP TO (£) totals vs SoW turnover",
  },
  int_missing: {
    title: "Won but no instalments",
    what: "Projects marked as won that have no invoice schedule yet.",
    how: "SoW projects at COMMISSIONED / SOW SIGNED / BRIEFED / IN FIELD / FINISHED / INVOICED with zero WiP instalment rows.",
    source: "SoW status vs WiP presence, joined on CID",
  },
  int_orphans: {
    title: "WiP-only projects",
    what: "Ledger rows whose CID doesn't exist on the Schedule of Work.",
    how: "Mostly pre-SoW-era projects. Informational - they still count in revenue.",
    source: "WiP CIDs not found on the SoW",
  },
  int_dupes: {
    title: "Duplicate FIIDs",
    what: "The same instalment ID appearing on more than one valued row.",
    how: "FIIDs counted per CID across rows with non-zero value. Both rows are currently counted in revenue (this matches your accounting export) - fix in SharePoint if one is spurious.",
    source: "Work in Progress list · FIID column",
  },
  // ------------------------------------------------------ Cash (QuickBooks)
  cash_on_hand: {
    title: "Cash on hand",
    what: "Live combined balance of every bank account in QuickBooks.",
    how: "Sum of CurrentBalance across accounts of type Bank. Credit card balances are listed separately and deducted from the net position, not from this figure.",
    source: "QuickBooks Online · Account (Bank)",
  },
  net_position: {
    title: "Net position",
    what: "What the company is really worth in near-term cash terms.",
    how: "Cash on hand + outstanding receivables (AR) − open supplier bills (AP) − credit card balances − the VAT set-aside.",
    source: "QuickBooks Online · Accounts, Invoices, Bills",
  },
  vat_set_aside: {
    title: "VAT set-aside",
    what: "Cash that isn't yours - VAT owed to HMRC that must sit ready.",
    how: "VAT quarters are INFERRED from your accountant's quarterly VAT journals on the VAT Control account (dated on each quarter's last day - currently a Feb-Apr / May-Jul / Aug-Oct / Nov-Jan stagger). The figure = the last completed quarter's return while its payment window is open (journal amount when booked, else output VAT minus input VAT estimated from invoice and bill tax details), plus anything already accrued this quarter (floored at £0 - most client invoices are zero-rated exports, so early-quarter accrual is often nil). Assumes each return is paid on its due date (~1 month + 7 days after the quarter end). Falls back to calendar quarters if no VAT journals exist.",
    source: "QuickBooks Online · VAT Control journals + TxnTaxDetail on Invoices and Bills",
  },
  cash_runway: {
    title: "Runway",
    what: "How many months the company could operate with no new income.",
    how: "Net position ÷ monthly burn. Burn is the average of total costs (expenses + cost of sales) over the last 3 complete months from the QuickBooks P&L.",
    source: "QuickBooks Online · ProfitAndLoss report",
  },
  cash_forecast: {
    title: "13-week cash-flow forecast",
    what: "Projected weekly cash movements and the resulting balance curve.",
    how: "Inflows: each open invoice lands on its due date shifted by that client's median payment lateness from paid-invoice history (book-wide median when a client has fewer than 2 paid invoices); anything already past its expected date lands this week. Outflows: open supplier bills on their due dates (overdue bills this week). Balance = cash on hand plus the running net of each week.",
    source: "QuickBooks Online · Invoices, Bills, Payments",
  },
  // ------------------------------------------------------ P&L (QuickBooks)
  pnl_income: {
    title: "Income · last 12 months",
    what: "Company-level income as booked in the accounts (accrual basis).",
    how: "Total Income row of the QuickBooks Profit & Loss report, summarised by month over the trailing 12 months. Unlike the WiP-based tabs this includes everything that hits the books, not just project instalments.",
    source: "QuickBooks Online · ProfitAndLoss report",
  },
  bs_net_assets: {
    title: "Net assets",
    what: "What the company is worth on paper: everything we own minus everything we owe. Equals the Capital & Reserves (equity) section of the balance sheet.",
    how: "Total assets (fixed + current) minus total liabilities (creditors due within one year + after one year), as of today. Compared with the same figure 12 months ago.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  bs_working_capital: {
    title: "Working capital",
    what: "The cushion available to fund day-to-day operations.",
    how: "Current assets (cash, debtors, other current assets) minus creditors falling due within one year - QBO's 'Net current assets' line. Positive and growing = healthy.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  bs_current_ratio: {
    title: "Current ratio",
    what: "Can we cover our short-term bills with short-term assets?",
    how: "(Current assets + recoverable balances) ÷ what we actually owe within one year (credit balances in the creditors section only). Rule of thumb: 1.5× or above is comfortable, below 1× means short-term obligations exceed short-term assets. Blank when less than £1k is owed, because the ratio would be meaningless.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  bs_quick: {
    title: "Quick ratio (acid test)",
    what: "The stricter liquidity test - only counts assets that are cash or nearly cash.",
    how: "(Cash at bank + debtors) ÷ what we actually owe within one year. Ignores recoverable tax, prepayments and other less-liquid current assets. 1× or above is comfortable. Blank when less than £1k is owed.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  bs_creditors: {
    title: "Due within 1 year",
    what: "What the company actually owes in the short term: suppliers, credit card, accruals, deferred tax, payroll controls.",
    how: "Sum of the credit balances in QuickBooks' Current Liabilities section. Debit balances in that section (VAT or corporation tax due back from HMRC, supplier overpayments) are not netted off here; they appear under Recoverable instead, so the two tiles together reproduce QuickBooks' own section total.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  bs_recoverable: {
    title: "Recoverable",
    what: "Money due back to the company that QuickBooks books as negative liabilities: VAT Control when input VAT exceeds output VAT, corporation tax paid ahead of the charge, suppliers we have overpaid.",
    how: "Sum of the debit balances found in the Current Liabilities section, shown positive. Counted as a current asset in the current ratio, excluded from the quick ratio.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  bs_gearing: {
    title: "Debt to equity",
    what: "How much of the company is financed by borrowing versus its own capital.",
    how: "Total liabilities (short + long term) ÷ equity. Below 1× means the company is financed mostly by its own capital; materially above 1.5× flags reliance on debt.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  bs_chart: {
    title: "Assets vs liabilities vs equity",
    what: "The month-end balance sheet picture over the last 12 months.",
    how: "Month-end balances from the QBO BalanceSheet report summarised by month. Assets = current + fixed; liabilities = creditors due within and after one year; the line is equity (net assets). The last point is today's partial-month position.",
    source: "QuickBooks Online · BalanceSheet report",
  },
  ar_vat_split: {
    title: "Debtors: net vs VAT",
    what: "The exact amount owed to us, split into the net (excl VAT) revenue and the VAT element.",
    how: "Sum of every open invoice's outstanding balance. Each invoice's VAT is taken from its QuickBooks tax detail; for partially-paid invoices the VAT is pro-rated by the share still outstanding. Net = outstanding − VAT.",
    source: "QuickBooks Online · open invoices (TxnTaxDetail)",
  },
  ap_vat_split: {
    title: "Creditors: net vs VAT",
    what: "The exact amount we owe suppliers, split into the net (excl VAT) cost and the reclaimable input VAT.",
    how: "Sum of every open bill's outstanding balance. Each bill's VAT is taken from its QuickBooks tax detail; for partially-paid bills the VAT is pro-rated by the share still outstanding. Net = outstanding − VAT.",
    source: "QuickBooks Online · open bills (TxnTaxDetail)",
  },
  cl_country: {
    title: "Turnover by country",
    what: "Where the last 12 months of turnover came from, by client country - the classic insurer / bank questionnaire breakdown.",
    how: "Every invoice and sales receipt (minus credit notes) dated in the trailing 12 months, net of VAT, grouped by the customer's billing-address country in QuickBooks. Customers with NO country set are assumed United Kingdom and their total is disclosed below the chart - set the country on the customer record in QuickBooks to refine it.",
    source: "QuickBooks Online · invoices + customer billing addresses",
  },
  cr_country: {
    title: "Expenditure by country",
    what: "The countries the business obtains goods or services from, with the share of spend attributable to each.",
    how: "Every bill and expense transaction (minus supplier credits and refunds) dated in the trailing 12 months, net of VAT, grouped by the supplier's billing-address country in QuickBooks. Suppliers with NO country set are assumed United Kingdom and their total is disclosed - set the country on the supplier record in QuickBooks to refine it.",
    source: "QuickBooks Online · bills + expenses + supplier billing addresses",
  },
  pnl_net: {
    title: "Net profit",
    what: "What's left after ALL costs and the below-the-line items: operating profit + investment result + other non-operating.",
    how: "Net Income row of the QuickBooks Profit & Loss report. This includes investment revaluations, so use Operating profit to judge the trading business.",
    source: "QuickBooks Online · ProfitAndLoss report",
  },
  pnl_operating: {
    title: "Operating profit",
    what: "Profit from the trading business only - income minus cost of sales and expenses, before any investment activity.",
    how: "Net Operating Income row of the QuickBooks Profit & Loss report (falls back to income − COGS − expenses). Excludes everything in the Other Income and Other Expenses sections.",
    source: "QuickBooks Online · ProfitAndLoss report",
  },
  pnl_investment: {
    title: "Investment result",
    what: "The war chest's contribution to net profit: portfolio revaluations, venture write-ups or downs, dividends and interest earned.",
    how: "Other Income and Other Expenses lines whose account name looks investment-related (invest / unrealised / realised / fair value / portfolio / dividend / revaluation / venture / FAS; interest counts only on the income side). Anything else below the line is shown separately as other non-operating.",
    source: "QuickBooks Online · ProfitAndLoss report",
  },
  // ------------------------------------------------------ Creditors (QuickBooks)
  dpo: {
    title: "DPO · days payable outstanding",
    what: "How long, on average, supplier bills sit before being paid.",
    how: "Open AP ÷ trailing 12-month supplier billing × 365.",
    source: "QuickBooks Online · Bills",
  },
  // ------------------------------------------------------ Reconciliation
  recon_match: {
    title: "WiP ↔ QuickBooks reconciliation",
    what: "Do the instalments marked INVOICED on the WiP ledger actually exist as invoices in the accounts - and vice versa?",
    how: "Each QBO invoice carries the master project number in its customer name, so invoices are matched to INVOICED WiP instalments by project number + amount (net of VAT first, then gross, then ±1%, then the closest instalment on the same project). Invoices whose project exists on the WiP ledger but has no free instalment still count as tied. Client name + amount matching remains as a fallback for rows without a project number. Foreign currency invoices are converted to GBP at each invoice's own exchange rate. Whatever remains on either side is listed as potential leakage.",
    source: "QuickBooks Online Invoices vs SharePoint Work in Progress list",
  },
};
